import { AuthBaseClient } from '../base-client';
import { startCallResponseSchema, type StartCallBody, type StartCallResponse } from './types';

/**
 * Client for the dev-tools `/api/browser-call/test` endpoint.
 *
 * Single network surface for the test-call flow so the FE doesn't
 * hand-roll `fetch()`. Validates the response shape with Zod (so a
 * malformed `recordingUrl` or missing `interactionId` is caught at the
 * boundary, not when the consumer touches the field) and threads the
 * `Idempotency-Key` header through the base-client request layer.
 */
export class BrowserCallClient extends AuthBaseClient {
  /**
   * Start a browser-test call. The body is the shared discriminated
   * union (`StartCallBody`); `idempotencyKey` is sent as an HTTP header
   * so the server can HMAC it against the caller's org id and produce
   * a stable `interactionId` across retries.
   *
   * Mount-once + StrictMode safety: pass `signal` from the caller's
   * useEffect cleanup so the first mount's in-flight fetch cancels
   * before the second mount's request fires.
   */
  async startTest(
    body: StartCallBody,
    opts: { idempotencyKey: string; signal?: AbortSignal }
  ): Promise<StartCallResponse> {
    const raw = await this.request<unknown>('/api/browser-call/test', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Idempotency-Key': opts.idempotencyKey,
      },
      signal: opts.signal,
    });
    // Zod parse at the boundary so the response contract is enforced
    // here, not at every call site that touches `interactionId`.
    return startCallResponseSchema.parse(raw);
  }
}
